/**
 * Main search module
 * Combines tokenizer, index, scorer, and query parser into a unified search API
 */

import type { Webpage } from '@/types';
import type { InvertedIndex } from './index';
import { buildIndex, addDocument, removeDocument, createIndex } from './index';
import { parseQuery, containsPhrase } from './query';
import { tokenize } from './tokenizer';
import {
  scoreDocuments,
  scoreDocumentsWithPrefix,
  filterRequiredTerms,
  filterExcludedTerms,
} from './scorer';
import {
  saveSearchIndex,
  loadSearchIndex,
  clearSearchIndex,
  isIndexValid,
} from './storage';

export interface SearchResult {
  webpage: Webpage;
  score: number;
}

export interface SearchEngine {
  // Core operations
  search: (query: string) => SearchResult[];
  rebuild: (webpages: Webpage[]) => void;
  
  // Incremental updates
  addWebpage: (webpage: Webpage) => void;
  removeWebpage: (webpageId: string) => void;
  updateWebpage: (webpage: Webpage) => void;
  
  // Persistence
  saveToStorage: () => Promise<void>;
  loadFromStorage: () => Promise<boolean>;
  clearStorage: () => Promise<void>;
  
  // State
  isReady: () => boolean;
  getDocCount: () => number;
}

/**
 * Create a search engine instance
 */
export function createSearchEngine(
  userId: string,
  initialWebpages?: Webpage[]
): SearchEngine {
  let index: InvertedIndex = createIndex();
  let webpageMap = new Map<string, Webpage>();
  let ready = false;

  // Initialize with webpages if provided
  if (initialWebpages && initialWebpages.length > 0) {
    rebuild(initialWebpages);
  }

  function rebuild(webpages: Webpage[]): void {
    index = buildIndex(webpages);
    webpageMap.clear();
    for (const wp of webpages) {
      webpageMap.set(wp.id, wp);
    }
    ready = true;
  }

  function addWebpage(webpage: Webpage): void {
    addDocument(index, webpage);
    webpageMap.set(webpage.id, webpage);
  }

  function removeWebpage(webpageId: string): void {
    removeDocument(index, webpageId);
    webpageMap.delete(webpageId);
  }

  function updateWebpage(webpage: Webpage): void {
    // Remove and re-add to update
    removeDocument(index, webpage.id);
    addDocument(index, webpage);
    webpageMap.set(webpage.id, webpage);
  }

  function search(query: string): SearchResult[] {
    if (!ready || !query.trim()) {
      // Return all webpages sorted by created_at if no query
      return Array.from(webpageMap.values())
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .map(webpage => ({ webpage, score: 0 }));
    }

    const parsed = parseQuery(query);

    if (parsed.isEmpty) {
      return Array.from(webpageMap.values())
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .map(webpage => ({ webpage, score: 0 }));
    }

    // Combine terms and phrase terms for scoring
    const allTerms = [...parsed.terms];
    for (const phrase of parsed.phrases) {
      allTerms.push(...phrase);
    }

    // Score documents
    let scored: Array<{ docId: string; score: number }>;

    if (parsed.prefixes.length > 0) {
      scored = scoreDocumentsWithPrefix(allTerms, parsed.prefixes, index);
    } else {
      scored = scoreDocuments(allTerms, index);
    }

    // Filter by required terms (all must match)
    if (parsed.terms.length > 0) {
      scored = filterRequiredTerms(scored, parsed.terms, index);
    }

    // Filter by excluded terms
    if (parsed.excluded.length > 0) {
      scored = filterExcludedTerms(scored, parsed.excluded, index);
    }

    // Filter by phrases (must contain exact phrase sequence)
    if (parsed.phrases.length > 0) {
      scored = scored.filter(({ docId }) => {
        const webpage = webpageMap.get(docId);
        if (!webpage) return false;

        // Tokenize all searchable text for this webpage
        const allText = [
          webpage.title,
          webpage.description,
          webpage.content,
          webpage.notes,
          webpage.site_name,
          webpage.author,
        ]
          .filter(Boolean)
          .join(' ');

        const docTokens = tokenize(allText);

        // Check all phrases are present
        return parsed.phrases.every(phrase => containsPhrase(docTokens, phrase));
      });
    }

    // Map back to webpages
    const results: SearchResult[] = [];

    for (const { docId, score } of scored) {
      const webpage = webpageMap.get(docId);
      if (webpage) {
        results.push({ webpage, score });
      }
    }

    return results;
  }

  async function saveToStorage(): Promise<void> {
    if (!ready) return;
    await saveSearchIndex(userId, index);
  }

  async function loadFromStorage(): Promise<boolean> {
    // Check if stored index is valid for current data
    const valid = await isIndexValid(userId, webpageMap.size);
    if (!valid) return false;

    const storedIndex = await loadSearchIndex(userId);
    if (storedIndex) {
      index = storedIndex;
      ready = true;
      return true;
    }

    return false;
  }

  async function clearStorage(): Promise<void> {
    await clearSearchIndex(userId);
  }

  function isReady(): boolean {
    return ready;
  }

  function getDocCount(): number {
    return index.totalDocs;
  }

  return {
    search,
    rebuild,
    addWebpage,
    removeWebpage,
    updateWebpage,
    saveToStorage,
    loadFromStorage,
    clearStorage,
    isReady,
    getDocCount,
  };
}

// Re-export commonly used types and functions
export { parseQuery } from './query';
export { tokenize } from './tokenizer';
export type { ParsedQuery } from './query';
export type { InvertedIndex } from './index';

